import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessagesSquare, Users, Send, ChevronLeft, Search, UserCircle2, Crown, Network
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import {
  getMyProfile,
  removeRepresentativeToken,
  getCommContacts,
  getCommConversations,
  openCommConversation,
  getCommMessages,
  sendCommMessage,
  markCommRead,
  RepCommContact,
  RepCommConversation,
  RepCommMessage,
  RepCommRelation
} from '../../services/representativeService';
import useRepresentativeCommSocket from '../../hooks/useRepresentativeCommSocket';
import AssociativeAppShell from '../../components/AssociativeAppShell';

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  caricaPrincipale?: string;
  foto?: string;
  associatesAccess?: boolean;
  permissions: { name: string; description: string }[];
}

const RELATION_GROUPS: { key: RepCommRelation; label: string; icon: React.ReactNode }[] = [
  { key: 'peer', label: 'Pari grado', icon: <Users size={12} /> },
  { key: 'superior', label: 'Riferimento territoriale', icon: <Crown size={12} /> },
  { key: 'subordinate', label: 'Il tuo territorio', icon: <Network size={12} /> },
];

const initialsOf = (nome?: string, cognome?: string) =>
  `${nome?.[0] || ''}${cognome?.[0] || ''}`.toUpperCase() || '–';

const timeShort = (iso: string) =>
  new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

const dayLabel = (iso: string): string => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Oggi';
  if (d.toDateString() === yesterday.toDateString()) return 'Ieri';
  return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
};

const conversationTitle = (conv: RepCommConversation): string => {
  if (conv.type === 'room') return conv.name || 'Stanza';
  const other = conv.participants[0];
  return other ? `${other.nome} ${other.cognome}` : 'Conversazione';
};

const AssociativeCommunications: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [profile, setProfile] = useState<RepProfile | null>(null);
  const [conversations, setConversations] = useState<RepCommConversation[]>([]);
  const [contacts, setContacts] = useState<RepCommContact[]>([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [tab, setTab] = useState<'chat' | 'contatti'>('chat');
  const [contactSearch, setContactSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;
  const [messages, setMessages] = useState<RepCommMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const msgsEndRef = useRef<HTMLDivElement | null>(null);
  const msgsBoxRef = useRef<HTMLDivElement | null>(null);
  // Evita l'autoscroll in fondo quando si prepende cronologia vecchia
  const skipAutoScrollRef = useRef(false);
  // Altezza del contenuto prima del prepend: serve a mantenere il punto di lettura
  const pendingScrollHeightRef = useRef<number | null>(null);
  // Specchio dei messaggi per dedup sincrono negli handler socket
  const messagesRef = useRef<RepCommMessage[]>([]);
  const [msgTotal, setMsgTotal] = useState(0);
  const [msgPage, setMsgPage] = useState(1);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const canLive = profile?.permissions?.some(p => p.name === 'associativa.live.publish') ?? false;
  const canCircolari = profile?.permissions?.some(p => p.name === 'associativa.circolari.read') ?? false;
  const canSondaggi = profile?.permissions?.some(p => p.name === 'associativa.sondaggi.read') ?? false;
  const canCalendario = profile?.permissions?.some(p => p.name === 'associativa.eventi.read') ?? false;

  // ===== Profilo (pattern comune area associativa) =====
  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }
    const loadProfile = async () => {
      try {
        const res = await getMyProfile();
        if (res.success) setProfile(res.data);
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore caricamento profilo');
      }
    };
    loadProfile();
  }, [navigate, toast]);

  // ===== Conversazioni e contatti =====
  const refreshConversations = useCallback(async () => {
    try {
      const res = await getCommConversations();
      if (res.success) setConversations(res.conversations);
    } catch {
      /* la lista resta quella precedente */
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [convRes, contactsRes] = await Promise.all([getCommConversations(), getCommContacts()]);
        if (convRes.success) setConversations(convRes.conversations);
        if (contactsRes.success) setContacts(contactsRes.contacts);
        setContactsLoaded(true);
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore caricamento comunicazioni');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate, toast]);

  // ===== Socket realtime =====
  const handleNewMessage = useCallback((message: RepCommMessage) => {
    if (String(message.conversation) === activeIdRef.current) {
      // Dedup sincrono: il totale cresce solo se il messaggio viene davvero appeso
      if (messagesRef.current.some(m => m._id === message._id)) return;
      setMessages(prev => (prev.some(m => m._id === message._id) ? prev : [...prev, message]));
      setMsgTotal(prev => prev + 1);
    }
  }, []);

  const handleConversationUpdated = useCallback(() => {
    refreshConversations();
  }, [refreshConversations]);

  // Il server ha scartato un invio socket: avvisa e ripristina il testo perso
  // (il server fa eco del content rifiutato: niente slot condivisi tra invii rapidi)
  const handleMessageError = useCallback((data: { conversationId: string; error: string; content?: string }) => {
    toast.error(data.error || 'Errore nell\'invio del messaggio');
    if (data.content) {
      // Il testo perso era stato scritto PRIMA di quello eventualmente in bozza
      const lost = data.content;
      setDraft(prev => (prev ? `${lost}\n${prev}` : lost));
    }
  }, [toast]);

  const {
    connected,
    onlineReps,
    typingUsers,
    joinConversation,
    leaveConversation,
    sendMessage: socketSend,
    startTyping,
    markRead
  } = useRepresentativeCommSocket({
    onNewMessage: handleNewMessage,
    onConversationUpdated: handleConversationUpdated,
    onMessageError: handleMessageError
  });

  // ===== Selezione conversazione =====
  const selectConversation = useCallback(async (conversationId: string) => {
    const previous = activeIdRef.current;
    if (previous && previous !== conversationId) leaveConversation(previous);
    setActiveId(conversationId);
    setMessages([]);
    setMsgTotal(0);
    setMsgPage(1);
    setMessagesLoading(true);
    joinConversation(conversationId);
    try {
      const res = await getCommMessages(conversationId);
      if (res.success) {
        setMessages(res.messages);
        setMsgTotal(res.total || res.messages.length);
      }
      // Un solo canale per il mark-read: socket se connesso, altrimenti REST
      if (connected) markRead(conversationId);
      else markCommRead(conversationId).catch(() => {});
      setConversations(prev => prev.map(c => (c._id === conversationId ? { ...c, unread: 0 } : c)));
    } catch {
      toast.error('Errore caricamento messaggi');
    } finally {
      setMessagesLoading(false);
    }
  }, [joinConversation, leaveConversation, markRead, connected, toast]);

  // Quando arriva un messaggio nella conversazione aperta, segna come letto
  useEffect(() => {
    if (!activeId || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (profile && last.sender?._id !== profile._id) {
      markRead(activeId);
      setConversations(prev => prev.map(c => (c._id === activeId ? { ...c, unread: 0 } : c)));
    }
  }, [messages, activeId, profile, markRead]);

  // Specchio sincrono dei messaggi (dedup negli handler socket)
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Autoscroll in fondo ad ogni cambio messaggi; quando invece si è prepesa
  // cronologia, mantieni il punto di lettura compensando lo scrollTop
  useLayoutEffect(() => {
    const box = msgsBoxRef.current;
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
      if (box && pendingScrollHeightRef.current !== null) {
        box.scrollTop += box.scrollHeight - pendingScrollHeightRef.current;
        pendingScrollHeightRef.current = null;
      }
      return;
    }
    msgsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, messagesLoading]);

  // Carica una pagina più vecchia di cronologia e la prepende al thread
  const loadOlderMessages = useCallback(async () => {
    if (!activeId || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const nextPage = msgPage + 1;
      const res = await getCommMessages(activeId, { page: String(nextPage) });
      if (res.success && res.messages.length > 0) {
        skipAutoScrollRef.current = true;
        pendingScrollHeightRef.current = msgsBoxRef.current?.scrollHeight ?? null;
        setMessages(prev => {
          const known = new Set(prev.map(m => m._id));
          const older = res.messages.filter((m: RepCommMessage) => !known.has(m._id));
          return [...older, ...prev];
        });
        setMsgPage(nextPage);
        setMsgTotal(res.total || msgTotal);
      }
    } catch {
      toast.error('Errore caricamento cronologia');
    } finally {
      setLoadingOlder(false);
    }
  }, [activeId, loadingOlder, msgPage, msgTotal, toast]);

  // ===== Apertura conversazione da contatto =====
  const openWithContact = useCallback(async (contact: RepCommContact) => {
    try {
      const res = await openCommConversation(contact._id);
      if (res.success) {
        await refreshConversations();
        setTab('chat');
        selectConversation(res.conversationId);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Impossibile aprire la conversazione');
    }
  }, [refreshConversations, selectConversation, toast]);

  // ===== Invio =====
  const handleSend = useCallback(async () => {
    const content = draft.trim();
    if (!content || !activeId || sending) return;
    setDraft('');
    if (socketSend(activeId, content)) return; // il messaggio torna via socket (o message_error col testo)
    // Fallback REST se il socket è giù
    setSending(true);
    try {
      const res = await sendCommMessage(activeId, content);
      if (res.success) {
        setMessages(prev => (prev.some(m => m._id === res.message._id) ? prev : [...prev, res.message]));
        refreshConversations();
      }
    } catch (err: any) {
      setDraft(content); // non perdere il testo scritto
      toast.error(err.response?.data?.error || 'Errore nell\'invio del messaggio');
    } finally {
      setSending(false);
    }
  }, [draft, activeId, sending, socketSend, refreshConversations, toast]);

  const handleComposerKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ===== Derivati =====
  const activeConv = conversations.find(c => c._id === activeId) || null;
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread || 0), 0);

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(c =>
      `${c.nome} ${c.cognome}`.toLowerCase().includes(q) ||
      (c.caricaPrincipale || '').toLowerCase().includes(q)
    );
  }, [contacts, contactSearch]);

  const isOnline = useCallback((repId?: string) => !!repId && onlineReps.includes(repId), [onlineReps]);

  const typingInActive = typingUsers.filter(t => t.representativeId !== profile?._id);

  // Raggruppa i messaggi per giorno (key con anno: l'etichetta "3 marzo" può
  // ripetersi tra anni diversi in cronologie lunghe)
  const groupedMessages = useMemo(() => {
    const groups: { key: string; day: string; items: RepCommMessage[] }[] = [];
    for (const msg of messages) {
      const key = new Date(msg.createdAt).toDateString();
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.items.push(msg);
      else groups.push({ key, day: dayLabel(msg.createdAt), items: [msg] });
    }
    return groups;
  }, [messages]);

  const hasOlderMessages = messages.length > 0 && messages.length < msgTotal;

  const renderAva = (conv: RepCommConversation) => {
    if (conv.type === 'room') {
      return <div className="comm-ava room"><Users size={16} /></div>;
    }
    const other = conv.participants[0];
    return (
      <div className="comm-ava">
        {initialsOf(other?.nome, other?.cognome)}
        {isOnline(other?._id) && <span className="comm-dot" aria-label="Online" />}
      </div>
    );
  };

  return (
    <AssociativeAppShell
      route="comunicazioni"
      nome={profile?.nome}
      cognome={profile?.cognome}
      caricaPrincipale={profile?.caricaPrincipale}
      tipo={profile?.tipo}
      foto={profile?.foto}
      associatesAccess={profile?.associatesAccess}
      canLive={canLive}
      canCircolari={canCircolari}
      canSondaggi={canSondaggi}
      canCalendario={canCalendario}
    >
      <div className="page fade-in comm-page">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <div>
            <div className="mono-label" style={{ color: 'var(--un-green-700)' }}>Area Rappresentativa</div>
            <h1 style={{ margin: '4px 0 0', font: '700 26px var(--font-display)', letterSpacing: 'var(--tracking-tight)' }}>
              Comunicazioni
            </h1>
          </div>
          <div className="spacer" style={{ flex: 1 }} />
          <span className={'comm-conn' + (connected ? ' on' : '')}>
            <span className="comm-conn-dot" />
            {connected ? 'In linea' : 'Offline'}
          </span>
        </div>

        <div className="comm-layout card" data-view={activeId ? 'thread' : 'list'}>
          {/* Colonna sinistra: chat + contatti */}
          <aside className="comm-side">
            <div className="comm-tabs">
              <div className="seg">
                <button
                  type="button"
                  aria-pressed={tab === 'chat'}
                  onClick={() => setTab('chat')}
                >
                  Chat{totalUnread > 0 ? ` (${totalUnread})` : ''}
                </button>
                <button
                  type="button"
                  aria-pressed={tab === 'contatti'}
                  onClick={() => setTab('contatti')}
                >
                  Contatti
                </button>
              </div>
            </div>

            {tab === 'chat' && (
              <div className="comm-list" role="list">
                {loading && <div className="comm-hint">Caricamento…</div>}
                {!loading && conversations.length === 0 && (
                  <div className="comm-hint">
                    Nessuna conversazione.<br />Apri la scheda <b>Contatti</b> per iniziare.
                  </div>
                )}
                {conversations.map(conv => (
                  <button
                    key={conv._id}
                    type="button"
                    role="listitem"
                    className="comm-item"
                    aria-current={conv._id === activeId ? 'true' : 'false'}
                    onClick={() => selectConversation(conv._id)}
                  >
                    {renderAva(conv)}
                    <span className="info">
                      <span className="n">{conversationTitle(conv)}</span>
                      <span className="p">
                        {conv.type === 'room' && <span className="room-tag">Stanza</span>}
                        {conv.lastMessage?.content || 'Nessun messaggio'}
                      </span>
                    </span>
                    <span className="meta">
                      {conv.lastMessage && <span className="t">{timeShort(conv.lastMessage.timestamp)}</span>}
                      {conv.unread > 0 && <span className="comm-unread">{conv.unread > 99 ? '99+' : conv.unread}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {tab === 'contatti' && (
              <div className="comm-list">
                <div className="comm-contact-search">
                  <div className="search">
                    <Search size={14} />
                    <input
                      placeholder="Cerca per nome o carica…"
                      value={contactSearch}
                      onChange={e => setContactSearch(e.target.value)}
                    />
                  </div>
                </div>
                {contactsLoaded && filteredContacts.length === 0 && (
                  <div className="comm-hint">Nessun contatto raggiungibile con le tue cariche.</div>
                )}
                {RELATION_GROUPS.map(group => {
                  const items = filteredContacts.filter(c => c.relation === group.key);
                  if (items.length === 0) return null;
                  return (
                    <React.Fragment key={group.key}>
                      <div className="comm-group-label">{group.icon} {group.label}</div>
                      {items.map(contact => (
                        <button
                          key={contact._id}
                          type="button"
                          className="comm-item"
                          onClick={() => openWithContact(contact)}
                        >
                          <span className="comm-ava">
                            {initialsOf(contact.nome, contact.cognome)}
                            {isOnline(contact._id) && <span className="comm-dot" aria-label="Online" />}
                          </span>
                          <span className="info">
                            <span className="n">{contact.nome} {contact.cognome}</span>
                            <span className="p">{contact.caricaPrincipale || 'Rappresentante'}</span>
                          </span>
                        </button>
                      ))}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </aside>

          {/* Thread */}
          <section className="comm-thread">
            {!activeConv && (
              <div className="comm-empty">
                <MessagesSquare size={34} />
                <div className="t">Le tue comunicazioni</div>
                <div className="d">
                  Scrivi ai rappresentanti con la tua stessa carica e al tuo riferimento territoriale.
                  Seleziona una conversazione o apri i contatti.
                </div>
              </div>
            )}

            {activeConv && (
              <>
                <div className="comm-thread-head">
                  <button
                    type="button"
                    className="comm-back"
                    onClick={() => { leaveConversation(activeConv._id); setActiveId(null); }}
                    aria-label="Torna alla lista"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  {renderAva(activeConv)}
                  <div className="who">
                    <div className="n">{conversationTitle(activeConv)}</div>
                    <div className="r">
                      {typingInActive.length > 0
                        ? `${typingInActive[0].name} sta scrivendo…`
                        : activeConv.type === 'room'
                          ? `${activeConv.participants.length + 1} partecipanti`
                          : activeConv.participants[0]?.caricaPrincipale || ''}
                    </div>
                  </div>
                </div>

                <div className="comm-msgs" ref={msgsBoxRef}>
                  {messagesLoading && <div className="comm-hint">Caricamento messaggi…</div>}
                  {!messagesLoading && messages.length === 0 && (
                    <div className="comm-hint">Nessun messaggio: scrivi il primo.</div>
                  )}
                  {hasOlderMessages && (
                    <button
                      type="button"
                      className="btn ghost sm comm-load-older"
                      onClick={loadOlderMessages}
                      disabled={loadingOlder}
                    >
                      {loadingOlder ? 'Caricamento…' : 'Carica messaggi precedenti'}
                    </button>
                  )}
                  {groupedMessages.map(group => (
                    <React.Fragment key={group.key}>
                      <div className="comm-day"><span>{group.day}</span></div>
                      {group.items.map(msg => {
                        const mine = msg.sender?._id === profile?._id;
                        return (
                          <div key={msg._id} className={'comm-msg' + (mine ? ' mine' : '')}>
                            {!mine && activeConv.type === 'room' && (
                              <div className="s">
                                <UserCircle2 size={11} /> {msg.sender?.nome} {msg.sender?.cognome}
                              </div>
                            )}
                            <div className="b">{msg.content}</div>
                            <div className="t">{timeShort(msg.createdAt)}</div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  <div ref={msgsEndRef} />
                </div>

                <div className="comm-composer">
                  <textarea
                    className="field"
                    rows={1}
                    placeholder="Scrivi un messaggio…  (Invio per inviare)"
                    value={draft}
                    maxLength={5000}
                    onChange={e => { setDraft(e.target.value); startTyping(activeConv._id); }}
                    onKeyDown={handleComposerKey}
                  />
                  <button
                    type="button"
                    className="btn primary comm-send"
                    onClick={handleSend}
                    disabled={!draft.trim() || sending}
                    aria-label="Invia messaggio"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </AssociativeAppShell>
  );
};

export default AssociativeCommunications;
